import React, { useState } from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Table, Tag, Space, Select, DatePicker } from 'antd';
import { LlmCallLogDto, LlmCallStatus } from '@wellverse/shared-ts';

const statusColors: Record<LlmCallStatus, string> = {
  [LlmCallStatus.SUCCESS]: '#52c41a',
  [LlmCallStatus.FAILED]: '#ff4d4f',
  [LlmCallStatus.TIMEOUT]: '#fa8c16',
  [LlmCallStatus.PENDING]: '#898989',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const LlmCallLogList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [providerFilter, setProviderFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string, string] | undefined>(undefined);

  const { tableProps } = useTable<LlmCallLogDto>({
    resource: 'llm-call-logs',
    filters: {
      permanent: [
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
        ...(providerFilter ? [{ field: 'providerId', operator: 'eq' as const, value: providerFilter }] : []),
        ...(dateRange ? [
          { field: 'dateFrom', operator: 'eq' as const, value: dateRange[0] },
          { field: 'dateTo', operator: 'eq' as const, value: dateRange[1] },
        ] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="llm-call-logs" canCreate={false}>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Select
          placeholder="Filter by provider"
          allowClear
          style={{ width: 200 }}
          onChange={(value) => setProviderFilter(value)}
          options={[]}
        />
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: 160 }}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { label: 'Success', value: LlmCallStatus.SUCCESS },
            { label: 'Failed', value: LlmCallStatus.FAILED },
            { label: 'Timeout', value: LlmCallStatus.TIMEOUT },
          ]}
        />
        <DatePicker.RangePicker
          onChange={(_, dateStrings) => {
            if (dateStrings && dateStrings[0] && dateStrings[1]) {
              setDateRange([dateStrings[0], dateStrings[1]]);
            } else {
              setDateRange(undefined);
            }
          }}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column
          dataIndex="createdAt"
          title="Date"
          width={180}
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="providerName"
          title="Provider"
          render={(name: string) => name || '—'}
        />
        <Table.Column dataIndex="model" title="Model" />
        <Table.Column
          dataIndex="endpoint"
          title="Endpoint"
          render={(endpoint: string) => endpoint ? (endpoint.length > 30 ? `${endpoint.slice(0, 30)}...` : endpoint) : '—'}
        />
        <Table.Column
          dataIndex="latencyMs"
          title="Latency (ms)"
          width={120}
          render={(ms: number) => ms !== undefined ? `${ms}ms` : '—'}
        />
        <Table.Column dataIndex="inputTokens" title="Input Tokens" width={120} render={(v: number) => v ?? '—'} />
        <Table.Column dataIndex="outputTokens" title="Output Tokens" width={120} render={(v: number) => v ?? '—'} />
        <Table.Column
          dataIndex="status"
          title="Status"
          width={120}
          render={(status: LlmCallStatus) => {
            const color = statusColors[status] || '#898989';
            return (
              <Tag style={tagStyle(color)}>
                {status}
              </Tag>
            );
          }}
        />
        <Table.Column
          dataIndex="errorMessage"
          title="Error"
          render={(msg: string) => msg ? (msg.length > 40 ? `${msg.slice(0, 40)}...` : msg) : '—'}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: LlmCallLogDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="llm-call-logs" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
